/**
 * @author NikolaV
 */
import * as React from 'react';
import { format, parseISO } from 'date-fns';
import { DateUtils } from '@shared/utils/DateUtils';
import { cs } from '@shared/utils/utils-classname';


export const DateTimeSpan: React.FC<{ timestamp: number | string; utc?: boolean; timezone?: boolean }> = ({ timestamp, utc = false, timezone = false }) => {
    const format = timezone ? 'Pp (OOOO)' : 'Pp';
    return formatDateGeneric(timestamp, utc, format);
};

export const DateSpan: React.FC<{ timestamp: number | string; utc?: boolean }> = ({ timestamp, utc = false }) => formatDateGeneric(timestamp, utc, 'P');

export function formatDateGeneric(timestampOrIso: string | number, utc, formatStr = 'Pp') {
    if (!timestampOrIso) return null;

    const date: Date = $.isNumeric(timestampOrIso) ? new Date(timestampOrIso) : parseISO(timestampOrIso as string);
    const longFormat = format(date, 'PPPPpppp', { locale: DateUtils.getCurrentLocale() });
    const shortFormat = format(date, formatStr, { locale: DateUtils.getCurrentLocale() });

    const timeAgo = (!!utc ? moment.utc(timestampOrIso) : moment(timestampOrIso)).fromNow();
    return <span title={longFormat + ' (' + timeAgo + ')'}>{shortFormat}</span>;
}

export type FormErrorsState = Record<string /* field name */, FormError>;

export interface FormError {
    type: string;
    message?: string;
    maxLength?: number;
    minOptions?: number;
}

export function FormErrors(props: { error: FormError; maxLength?: number; minOptions?: number }) {
    if (!props?.error) return null;
    const typeError = {
        required: __('general.validation.cannot_be_empty'),
        maxLength: __('general.validation.maximum_n_field_length', props.maxLength ?? props.error?.maxLength),
        pattern: __('general.validation.invalid_field_value'),
        minOptions: __('general.validation.n_minimum_selected_options_required', props.minOptions ?? props.error?.minOptions),
    };
    return (
        <div style={{ color: 'red', marginTop: 2 }}>
            <span>{props.error?.message || typeError[props.error?.type]}</span>
        </div>
    );
}

/*
 * Shows spinner while <img> is loading
 */
export class ImgLoader extends React.Component<
    {
        src: string;
        className?: string;
        maxWidth?: string;
        onLoad: (image: HTMLImageElement) => void;
    },
    {}
> {
    state = {
        loaded: false,
    };

    el: HTMLImageElement = null;

    handleOnLoad = () => {
        this.setState({ loaded: true });
        if (typeof this.props.onLoad === 'function') {
            this.props.onLoad(this.el);
        }
    };

    render() {
        return (
            <div style={!this.state.loaded ? { minHeight: 100 } : null} className={this.state.loaded ? null : 'overlay-loading'}>
                <img src={this.props.src} className={this.props.className} style={{ maxWidth: this.props.maxWidth }} onLoad={this.handleOnLoad} ref={el => (this.el = el)} />
            </div>
        );
    }
}

export class FormGroup extends React.Component<
    {
        title: string;
        checkable?: boolean;
        checked?: boolean;
        tight?: boolean;
        row?: boolean;
        onChecked?: (state: boolean) => void;
        style?: object;
        children?: React.ReactNode;
    },
    {
        checked?: boolean;
    }
> {
    state = {
        checked: this.props.checkable ? false : null,
    };

    el: HTMLDivElement = null;

    render() {
        const { title, checkable, tight } = this.props;
        const checked = this.isControlledComponent() ? this.props.checked : this.state.checked;

        if (tight) {
            return (
                <div className='form-group-tight'>
                    <label style={{ margin: '0px' }}>
                        {checkable && <input type='checkbox' checked={checked === true} onChange={this.handleCheck} />} {title}
                    </label>
                    <div ref={e => (this.el = e)}>{this.props.children}</div>
                </div>
            );
        }

        return (
            <div className={cs('form-group', { 'is-row': this.props.row })} style={this.props.style}>
                <label>
                    {checkable && <input type='checkbox' checked={checked === true} onChange={this.handleCheck} />} {title}
                </label>
                <div ref={e => (this.el = e)}>{this.props.children}</div>
            </div>
        );
    }

    handleCheck = e => {
        const { checked } = e.currentTarget;

        if (this.isControlledComponent()) {
            this.props.onChecked(checked);
        } else {
            this.setState({
                checked,
            });
        }
    };

    componentDidMount() {
        this.enableDisableInputs();
    }

    componentDidUpdate() {
        this.enableDisableInputs();
    }

    enableDisableInputs() {
        // Can't use fieldset, see bug:
        // https://connect.microsoft.com/IE/feedbackdetail/view/962368/can-still-edit-input-type-text-within-fieldset-disabled

        const $els = $(this.el).find('input, select, textarea, button');

        if (this.props.checkable) {
            $els.enableDisable(this.isControlledComponent() ? this.props.checked : this.state.checked);
        } else {
            $els.enableDisable(true);
        }
    }

    private isControlledComponent() {
        return this.props.hasOwnProperty('checked') && typeof this.props.onChecked === 'function';
    }
}
